import { useMemo } from "react";
import { Any } from "@cede/types";
import { cherryPickState, makeFirstLettersCapital } from "@cede/utils";
import { ARRAY_SEPARATOR, OPTIONAL_MARKER, SELECT_MARKER, StoreMethodMetaData } from "../../constants/methods";
import { WidgetConfigMetaData } from "../../constants/widgetConfig";
import { useCedeStore } from "../../hooks/useCedeStore";
import { useParamsForms } from "../../hooks/useParamsForms/useParamsForms";
import { getFormValue } from "../../hooks/useParamsForms/utils";
import { AccountIdsField } from "./fields/AccountIdsField";
import { AccountOrVaultIdField } from "./fields/AccountOrVaultIdField";
import { BooleanField } from "./fields/BooleanField";
import { SelectField } from "./fields/SelectField";
import { TextField } from "@mui/material";

type UserFieldGroupProps = {
  method: StoreMethodMetaData | WidgetConfigMetaData;
  id: string;
};

function displayType(type: string) {
  let value = makeFirstLettersCapital(type);
  if (value.includes(OPTIONAL_MARKER)) {
    value = value.replace(OPTIONAL_MARKER, " (optional)");
  }
  return value;
}
/**
 * @description This function is used to get the value of a form field. If the value is an array, it will
 * be joined by a space.
 */
function getSafeStringValue(method: StoreMethodMetaData | WidgetConfigMetaData, property: string, form: Any) {
  const value = getFormValue(method, property, form);
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(ARRAY_SEPARATOR);
  return value;
}

export const UserFieldGroup = ({ method }: UserFieldGroupProps) => {
  const { vaults, accounts } = useCedeStore(cherryPickState(["vaults", "accounts"]));
  const form = useParamsForms((state) => state.forms[method.methodName]);
  const setFormValue = useParamsForms((state) => state.setFormValue);

  const filteredAccounts = useMemo(() => {
    if (form?.vaultId) {
      return accounts.filter((account) => account.vaultId === form.vaultId);
    }
    return accounts;
  }, [form?.vaultId, accounts]);

  if (!method.params) return null;

  return (
    <>
      {Object.entries(method.params).map(([p, type]) => {
        if (type.includes("boolean")) {
          return (
            <BooleanField
              key={p}
              property={p}
              value={getFormValue(method, p, form)}
              onToggle={() => setFormValue(method, p, !getFormValue(method, p, form))}
            />
          );
        }

        if (type.includes(SELECT_MARKER)) {
          return (
            <SelectField
              key={p}
              property={p}
              value={getFormValue(method, p, form)}
              items={type.split(SELECT_MARKER)}
              type="String"
              onChange={(e) => setFormValue(method, p, e.target.value)}
            />
          );
        }

        if (["vaultId", "accountId"].includes(p)) {
          return (
            <AccountOrVaultIdField
              key={p}
              accounts={filteredAccounts}
              vaults={vaults}
              property={p}
              value={getFormValue(method, p, form)}
              onChange={(e) => setFormValue(method, p, e.target.value)}
            />
          );
        }

        if (p === "accountIds") {
          return (
            <AccountIdsField
              key={p}
              property={p}
              accounts={filteredAccounts}
              onChange={(e) => setFormValue(method, p, e.target.value)}
              value={getFormValue(method, p, form)}
              type={displayType(type)}
            />
          );
        }

        return (
          <TextField
            key={p}
            label={p}
            value={getSafeStringValue(method, p, form) ?? ""}
            onChange={(e) => setFormValue(method, p, e.target.value)}
            helperText={displayType(type)}
          />
        );
      })}
    </>
  );
};
